import Redirect from './Redirect';
import {
  useAuth,
  useAuthRoles,
  type AppPermission,
} from '@presov.sk/presov-auth-react';
import { Paper, Typography } from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = React.PropsWithChildren<{
  hasSomePermission?: AppPermission[];
  hasEveryPermission?: AppPermission[];
  redirect?: string;
  message?: string;
  /**
   * Prístup, ktorý nestojí na oprávnení - napríklad správca kategórie, ktorý
   * `pruser:report-categories:manage` nemá, ale svoju kategóriu spravovať
   * smie. `true` pustí aj bez oprávnení, `undefined` znamená „ešte sa to
   * nevie" a guard kým sa to nevie, nekreslí nič namiesto presmerovania.
   * Bez tohto propu sa guard správa ako doteraz.
   */
  orAllow?: boolean | undefined;
}>;

const PermissionsGuard: React.FC<Props> = ({
  children,
  hasSomePermission: hasSomePermissionProp = [],
  hasEveryPermission: hasEveryPermissionProps = [],
  redirect,
  message,
  ...rest
}) => {
  const { hasSomePermission, hasEveryPermission, isLoading } = useAuthRoles();
  const { authState } = useAuth();

  if (authState !== 'authorized') {
    return null;
  }

  const deniedByPermissions =
    (hasSomePermissionProp.length &&
      !hasSomePermission(hasSomePermissionProp)) ||
    (hasEveryPermissionProps.length &&
      !hasEveryPermission(hasEveryPermissionProps));

  const hasOrAllow = 'orAllow' in rest;
  const { orAllow } = rest;

  if (
    !isLoading &&
    deniedByPermissions &&
    hasOrAllow &&
    orAllow === undefined
  ) {
    return null;
  }

  const isDenied = deniedByPermissions && !(hasOrAllow && orAllow === true);

  if (!isLoading && isDenied) {
    if (redirect) {
      return <Redirect to={redirect} />;
    }

    if (message) {
      return (
        <Paper
          sx={{
            py: 4,
            px: 2,
          }}
        >
          <Typography
            sx={{
              color: (theme) => theme.palette.text.secondary,
              textAlign: 'center',
            }}
          >
            {message}
          </Typography>
        </Paper>
      );
    }

    return null;
  }

  return children;
};

export default PermissionsGuard;
